import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import { useQuery } from 'react-query';
import { baseUrl } from '../../constant/config';

/**
 * The accounts the connected token can actually publish to.
 *
 * Read live from the Graph API every time rather than from configuration.
 * Page ids used to be typed into .env by hand, which is how a personal profile
 * and then an app id both ended up in FACEBOOK_PAGE_ID - neither of which can
 * be posted to. Meta is the only thing that knows the answer, so it is asked.
 */
export default function ConnectedAccountsPanel({ enabled }) {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
    ['platformAccounts'],
    async () => {
      const { data } = await axios.get(`${baseUrl}/api/videos/platform-accounts`);
      return data.data.accounts;
    },
    { enabled, refetchOnWindowFocus: false, retry: false, staleTime: 60 * 1000 }
  );

  if (!enabled) return null;

  if (isLoading) {
    return (
      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="circular" width={44} height={44} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton height={20} width="30%" />
            <Skeleton height={16} width="20%" />
          </Box>
        </Stack>
      </Card>
    );
  }

  // Nothing connected yet is the normal first-run state, not an error worth
  // shouting about - the token banner below already explains what to do.
  if (isError) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {error?.response?.data?.message || error?.message}
      </Alert>
    );
  }

  const accounts = data || [];

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">
          {accounts.length === 1 ? 'Publishing to' : `${accounts.length} accounts available`}
        </Typography>
        <Button
          size="small"
          onClick={() => refetch()}
          disabled={isFetching}
          startIcon={isFetching ? <CircularProgress size={13} /> : <RefreshIcon fontSize="small" />}
        >
          Refresh
        </Button>
      </Stack>

      {accounts.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          This token does not administer any Facebook Page.
        </Typography>
      )}

      <Stack spacing={1.5}>
        {accounts.map((account) => (
          <Stack key={account.id} direction="row" spacing={2} alignItems="center">
            <Avatar src={account.pictureUrl || undefined} sx={{ width: 44, height: 44 }}>
              {account.name?.[0] || '?'}
            </Avatar>

            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <FacebookIcon sx={{ fontSize: 18, color: '#1877F2' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {account.name}
                </Typography>
                {account.active && (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    icon={<CheckCircleIcon />}
                    label="active"
                  />
                )}
              </Stack>

              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontFamily: 'monospace', display: 'block' }}
              >
                {account.id}
              </Typography>
            </Box>

            {account.instagram ? (
              <Tooltip title={`Instagram ${account.instagram.id}`}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<InstagramIcon sx={{ color: '#E1306C' }} />}
                  label={
                    account.instagram.username ? `@${account.instagram.username}` : 'linked'
                  }
                />
              </Tooltip>
            ) : (
              <Tooltip title="Link an Instagram Business account to this Page in Meta Business Suite to publish Reels">
                <Chip size="small" label="no Instagram" />
              </Tooltip>
            )}
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}

ConnectedAccountsPanel.propTypes = {
  enabled: PropTypes.bool,
};
